import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/common/form-field";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import type { WikiPage, WikiPageInput } from "@/features/knowledge/types";

const schema = Yup.object({
  pageTitle: Yup.string().trim().required("A title is required.").max(80),
  entryType: Yup.string().trim().required("An entry type is required.").max(20),
  pageSlug: Yup.string()
    .trim()
    .lowercase()
    .matches(/^[-\w]+$/, "Letters, numbers, hyphens and underscores only.")
    .required("A slug is required.")
    .max(20),
  content: Yup.string()
    .transform((v) => v?.replace(/<[^>]*>/g, "").trim())
    .required("Content is required."),
});

function ContentField() {
  const [field, meta, helpers] = useField<string>("content");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label>Content</Label>
      <RichTextEditor value={field.value} onChange={(html) => helpers.setValue(html)} />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface WikiFormDialogProps {
  page: WikiPage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: WikiPageInput) => Promise<void>;
}

export function WikiFormDialog({ page, open, onOpenChange, onSubmit }: WikiFormDialogProps) {
  const initial: WikiPageInput = {
    entryType: page?.entryType ?? "wiki",
    pageTitle: page?.pageTitle ?? "",
    pageSlug: page?.pageSlug ?? "",
    content: page?.content ?? "",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{page ? "Edit Wiki Page" : "Add Wiki Page"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={initial}
          validationSchema={schema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSubmit(values);
              onOpenChange(false);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <FormField name="pageTitle" label="Title" />
              <div className="grid grid-cols-2 gap-4">
                <FormField name="entryType" label="Entry type" placeholder="wiki" />
                <FormField name="pageSlug" label="Slug" placeholder="getting-started" />
              </div>
              <ContentField />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
